import type { NextPage } from 'next';
import { useMemo } from 'react';

import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import BottomBannerContainer from '@src/components/BottomBannerContainer';
import { PageContext } from '@src/config';
import type { IPageContextConfig } from '@src/config/PageContext';
import { BottomBannerTypes } from '@src/constants/bottomBannerTypes';
import { ListKeys } from '@src/constants/microcredits';
import ProductList from '@src/containers/list/ProductList';
import { useBestProducts } from '@src/hooks/useBestProducts';
import { useListPage } from '@src/hooks/useListPage';
import { Layout as AppLayout } from 'app/Layout';
import { AmountFilter } from 'features/AmountFilter';
import { AnchorLinkList } from 'shared/ui';
import { BrandingHeaders } from 'widgets/BrandingHeaders';
import { FullDealEntryPoint } from 'widgets/FullDealEntryPoint';
import { useIsShowFullDealEntrypoint } from 'widgets/FullDealEntryPoint/lib';
import { LightweightCard } from 'widgets/LightweightCard';
import { SeoComponents } from 'widgets/SeoComponents';

import { getFullDealAndPing3OffersPageData } from '../lib';

import styles from './FullDealAndPing3OffersPage.module.scss';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.FULL_DEAL_AND_PING3_OFFERS,
  source: 'search',
  sortPanelVisible: true,
  seoLinksVisible: true,
  extraFiltersVisible: true,
  cardTermIsVisible: true,
  withBanners: true,
  withInvitationBanner: true,
  withEmailBurner: true,
  withSelectionBanner: true,
  withCardProductLink: true,
};

export const FullDealAndPing3OffersPage: NextPage = () => {
  const isMobile = useIsMobile();
  const { filter, seo, pathname, hasUtmLabel } = useListPage(ListKeys.FULL_DEAL_AND_PING3_OFFERS);
  const config = useMemo(() => ({ ...pageConfig, withMonetizationCardLogoClick: isMobile }), [isMobile]);

  useBestProducts();

  const isShowFullDealEntrypoint = useIsShowFullDealEntrypoint();

  return (
    <AppLayout>
      <PageContext.Provider value={config}>
        <BrandingHeaders
          containerClassName={styles.page_header}
          afterHeaders={
            isShowFullDealEntrypoint ? (
              <FullDealEntryPoint heading={seo?.heading} />
            ) : (
              <AmountFilter filters={filter} listKey={ListKeys.FULL_DEAL_AND_PING3_OFFERS} />
            )
          }
        />

        {!hasUtmLabel && <AnchorLinkList />}

        <ProductList
          className={styles.product_list}
          filter={filter}
          gridLayout
          pathname={pathname}
          showMoreButton={undefined}
          renderCard={(props) => <LightweightCard {...props} hasUtmLabel={hasUtmLabel} />}
        />

        {seo && (
          <SeoComponents
            seo={seo}
            breadcrumbs={seo.breadcrumbs}
            prefooter={seo.prefooter}
            showAnchorLinks={isShowFullDealEntrypoint}
            showSubheader={false}
          />
        )}
        <BottomBannerContainer enabledTypes={[BottomBannerTypes.OUT_OF_PAGE, BottomBannerTypes.SIDEBAR]} />
      </PageContext.Provider>
    </AppLayout>
  );
};

FullDealAndPing3OffersPage.getInitialProps = (ctx: Application.ReduxNextPageContext) =>
  getFullDealAndPing3OffersPageData(ctx);
